// Shared UI kit for the tools platform — reuses the site design language.
import { useState } from "react";

export const GRID = "max-w-7xl mx-auto px-6 sm:px-8 md:px-12";

export function Field({ label, hint, prefix, suffix, value, onChange, type = "number", min, max, step, testid }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-zinc-800">{label}</span>
      {hint && <span className="block text-xs text-zinc-500 mt-0.5">{hint}</span>}
      <div className="mt-1.5 flex items-center border border-zinc-300 bg-white focus-within:border-black transition-colors">
        {prefix && <span className="pl-3 text-sm text-zinc-500">{prefix}</span>}
        <input
          type={type}
          inputMode={type === "number" ? "decimal" : undefined}
          data-testid={testid}
          className="w-full px-3 py-2.5 text-sm outline-none bg-transparent"
          value={value}
          min={min} max={max} step={step}
          onChange={(e) => onChange(e.target.value)}
        />
        {suffix && <span className="pr-3 text-sm text-zinc-500">{suffix}</span>}
      </div>
    </label>
  );
}

export function SliderField({ label, hint, value, onChange, min = 0, max = 100, step = 1, suffix = "%", testid }) {
  return (
    <div>
      <div className="flex items-baseline justify-between">
        <span className="text-sm font-medium text-zinc-800">{label}</span>
        <span className="text-sm font-semibold text-black tabular-nums">{value}{suffix}</span>
      </div>
      {hint && <span className="block text-xs text-zinc-500 mt-0.5">{hint}</span>}
      <input
        type="range" min={min} max={max} step={step} value={value}
        data-testid={testid}
        onChange={(e) => onChange(Number(e.target.value))}
        className="mt-2 w-full accent-black"
      />
    </div>
  );
}

export function ResultRow({ label, value, strong, tone }) {
  return (
    <div data-report-row className={`flex items-baseline justify-between gap-6 py-3 ${strong ? "border-t-2 border-black" : "border-t border-black/10"}`}>
      <span className={`text-sm ${strong ? "font-semibold text-black" : "text-zinc-600"}`}>{label}</span>
      <span className={`tabular-nums ${strong ? "font-serif-display text-2xl" : "text-sm font-medium"} ${tone === "bad" ? "text-red-700" : tone === "good" ? "text-emerald-700" : "text-black"}`}>
        {value}
      </span>
    </div>
  );
}

export function Waterfall({ items }) {
  const max = Math.max(...items.map((i) => Math.abs(i.value)), 1);
  return (
    <div className="space-y-1.5" data-testid="waterfall">
      {items.map((i) => (
        <div key={i.label} className="grid grid-cols-12 items-center gap-3">
          <span className="col-span-4 text-xs text-zinc-600 truncate">{i.label}</span>
          <div className="col-span-6 h-6 bg-zinc-100 relative">
            <div
              className={`h-full ${i.total ? "bg-black" : i.value < 0 ? "bg-zinc-400" : "bg-zinc-700"}`}
              style={{ width: `${Math.max(2, Math.abs(i.value) / max * 100)}%` }}
            />
          </div>
          <span className={`col-span-2 text-right text-xs tabular-nums ${i.total ? "font-semibold text-black" : "text-zinc-600"}`}>
            {i.display ?? i.value}
          </span>
        </div>
      ))}
    </div>
  );
}

export function Radar({ dims }) {
  // dims: [{label, score (0-100)}]
  const n = dims.length;
  const cx = 130, cy = 130, R = 100;
  const pt = (i, r) => {
    const a = (Math.PI * 2 * i) / n - Math.PI / 2;
    return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
  };
  const ring = (f) => dims.map((_, i) => pt(i, R * f).join(",")).join(" ");
  const shape = dims.map((d, i) => pt(i, (R * d.score) / 100).join(",")).join(" ");
  return (
    <svg viewBox="0 0 260 260" className="w-full max-w-[320px] mx-auto" data-testid="radar" role="img" aria-label="Scores radar chart">
      {[0.33, 0.66, 1].map((f) => (
        <polygon key={f} points={ring(f)} fill="none" stroke="#e4e4e7" strokeWidth="1" />
      ))}
      {dims.map((d, i) => {
        const [x, y] = pt(i, R);
        const [lx, ly] = pt(i, R + 16);
        return (
          <g key={d.label}>
            <line x1={cx} y1={cy} x2={x} y2={y} stroke="#e4e4e7" />
            <text x={lx} y={ly} textAnchor="middle" dominantBaseline="middle" fontSize="8.5" fill="#52525b">
              {d.label}
            </text>
          </g>
        );
      })}
      <polygon points={shape} fill="rgba(0,0,0,0.12)" stroke="#000" strokeWidth="1.5" />
    </svg>
  );
}

export function Note({ children, tone = "info" }) {
  return (
    <div className={`border-l-2 pl-4 py-1 text-sm leading-relaxed ${tone === "warn" ? "border-amber-500 text-amber-900" : "border-zinc-300 text-zinc-600"}`}>
      {children}
    </div>
  );
}

export function ToolSection({ title, children, testid }) {
  return (
    <div className="mt-10" data-testid={testid}>
      <h3 className="font-serif-display text-xl md:text-2xl text-black">{title}</h3>
      <div className="mt-4">{children}</div>
    </div>
  );
}

export function useNumbers(initial) {
  const [vals, setVals] = useState(initial);
  const set = (k) => (v) => setVals((s) => ({ ...s, [k]: v }));
  return [vals, set];
}

/** Copy text with a non-clipboard fallback (iframe/permission-safe). Resolves true on success. */
export async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      const okCopy = document.execCommand("copy");
      document.body.removeChild(ta);
      return okCopy;
    } catch {
      return false;
    }
  }
}
